import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';

import { AnalyticsUser } from './@types.js';

const ID_COOKIE_NAME = 'ro_ruid';
const ANON_ID_COOKIE_NAME = 'ro_anon_id';
const SEGMENT_ID_COOKIE_NAME = 'ajs_user_id';
const SEGMENT_ANON_ID_COOKIE_NAME = 'ajs_anonymous_id';
const ID_SESSION_STORAGE_KEY = '@remento/id-repository/ruid';
const ANON_ID_SESSION_STORAGE_KEY = '@remento/id-repository/anon_id';

export class IDRepository {
  private cookieSettings: { expires: 365; domain: string; sameSite: 'lax' };
  constructor(domain: string) {
    this.cookieSettings = {
      expires: 365,
      domain,
      sameSite: 'lax',
    } as const;
  }

  saveForSession(user: AnalyticsUser): AnalyticsUser {
    if (user.type == 'identified') {
      sessionStorage.setItem(ID_SESSION_STORAGE_KEY, user.id);
      if (user.anonymousId) {
        sessionStorage.setItem(ANON_ID_SESSION_STORAGE_KEY, user.anonymousId);
      }
    } else {
      sessionStorage.setItem(ANON_ID_SESSION_STORAGE_KEY, user.id);
    }

    if (user.type === 'identified') {
      return {
        ...user,
        source: 'session',
        isTemporary: true,
      };
    }

    return {
      ...user,
      source: 'session',
    };
  }

  save(user: AnalyticsUser): AnalyticsUser {
    if (user.type == 'identified') {
      Cookies.set(ID_COOKIE_NAME, user.id, this.cookieSettings);
      if (user.anonymousId) {
        Cookies.set(ANON_ID_COOKIE_NAME, user.anonymousId, this.cookieSettings);
      }
    } else {
      Cookies.set(ANON_ID_COOKIE_NAME, user.id, this.cookieSettings);
    }
    return {
      ...user,
      source: 'cookies',
    };
  }

  /**
   * Retrieves or creates an AnalyticsUser.
   *
   * @returns The resulting AnalyticsUser.
   */
  get(): AnalyticsUser {
    let user: AnalyticsUser;

    // If there is an RUID in the URL it always takes priority
    const params = new URLSearchParams(window.location.search);
    const utmRuid = params.get('utm_ruid');

    // Retrieve any temporary ID
    const sessionRuid = sessionStorage.getItem(ID_SESSION_STORAGE_KEY);
    const sessionAnonymousId = sessionStorage.getItem(ANON_ID_SESSION_STORAGE_KEY);

    // Fallback to the ajs (Segment) cookies since not all properties use the Remento cookies
    const cookieRuid = Cookies.get(ID_COOKIE_NAME) ?? Cookies.get(SEGMENT_ID_COOKIE_NAME);
    const cookieAnonymousId = Cookies.get(ANON_ID_COOKIE_NAME) ?? Cookies.get(SEGMENT_ANON_ID_COOKIE_NAME) ?? null;

    if (utmRuid != null) {
      user = {
        id: utmRuid,
        type: 'identified',
        traits: {},
        // Only associate the UTM RUID with the current anonymous ID if it wasn't already claimed by a cookie RUID
        anonymousId: cookieRuid != null && cookieRuid != utmRuid ? uuid() : cookieAnonymousId,
        source: 'utm',
      };
    } else if (sessionRuid != null) {
      user = {
        id: sessionRuid,
        type: 'identified',
        traits: {},
        anonymousId: sessionAnonymousId,
        source: 'session',
        isTemporary: true,
      };
    } else if (cookieRuid != null) {
      user = { id: cookieRuid, type: 'identified', traits: {}, anonymousId: cookieAnonymousId, source: 'cookies' };
    } else if (cookieAnonymousId != null) {
      user = { id: cookieAnonymousId, type: 'anonymous', traits: {}, source: 'cookies' };
    } else {
      user = { id: uuid(), type: 'anonymous', traits: {}, source: 'new' };
    }

    return user;
  }
}
